import type {
  InfiniteData,
  MutationFunctionContext,
  QueryClient,
} from '@tanstack/react-query';
import { Clock, Effect, Predicate } from 'effect';
import { type AudioItem, audioItemsBaseKey } from '../audio-items';
import { type AppRuntime, useAppRuntime } from '../runtime-context';
import { processingQueueQueryKey } from './audio-list-hooks';

// Central optimistic "walk" over the KNOWN, FINITE set of audio caches — the
// hand-rolled stand-in for a TanStack DB collection's optimistic mutators while
// db#709 (SSR) is unavailable. Every audio cache lives under the `['audioItems']`
// prefix and is created only by the named read hooks (`audio-list-hooks.ts`), so
// a prefix sweep here is guaranteed to find ALL of them — that's the whole point
// of the "no arbitrary list options" invariant. On a mutation we patch / remove
// the row by id across every cache that holds it (whatever its shape), snapshot
// only the caches we touched, roll back on error, and invalidate the prefix on
// settle so the server reconciles list membership. See
// docs/projects/complete/web/query-stack-route-loaders.md ("Scaling problem + decision").

// The three cache shapes under `['audioItems']`:
//   - InfiniteData<PageResult>  — library / processing / folder (`.pages`)
//   - PageResult                — continue / uploadsThisWeek (`.items`)
//   - AudioItem-ish object      — detail(id) / summary(id) (`.id`)
type PageResult = { items: ReadonlyArray<AudioItem>; nextCursor?: string };
type AudioRowLike = { id: string };

export type AudioCacheOp =
  | { kind: 'patchFields'; id: string; partial: Partial<AudioItem> }
  | { kind: 'removeById'; id: string };

// —— Pure shape-aware apply (returns the SAME reference when nothing changed,
//    so subscribers of untouched caches don't re-render) ——

const applyToItems = (
  items: ReadonlyArray<AudioItem>,
  op: AudioCacheOp,
): ReadonlyArray<AudioItem> => {
  if (op.kind === 'removeById') {
    const next = items.filter((i) => i.id !== op.id);
    return next.length === items.length ? items : next;
  }
  let changed = false;
  const next = items.map((i) => {
    if (i.id !== op.id) return i;
    changed = true;
    return { ...i, ...op.partial };
  });
  return changed ? next : items;
};

const applyToPage = (page: PageResult, op: AudioCacheOp): PageResult => {
  const items = applyToItems(page.items, op);
  return items === page.items ? page : { ...page, items };
};

export const applyOpToData = (data: unknown, op: AudioCacheOp): unknown => {
  if (!Predicate.isObjectKeyword(data)) return data;

  if ('pages' in data) {
    const infinite = data as InfiniteData<PageResult>;
    let changed = false;
    const pages = infinite.pages.map((p) => {
      const next = applyToPage(p, op);
      if (next !== p) changed = true;
      return next;
    });
    return changed ? { ...infinite, pages } : infinite;
  }

  if ('items' in data && Array.isArray((data as PageResult).items)) {
    return applyToPage(data as PageResult, op);
  }

  if ('id' in data) {
    // Single detail/summary object. Patch in place; never *remove* a single-row
    // cache (an open detail of a deleted row is handled by route/404 on the
    // invalidate, not the walk).
    const obj = data as AudioRowLike;
    if (op.kind === 'patchFields' && obj.id === op.id) {
      return { ...obj, ...op.partial };
    }
    return data;
  }

  return data;
};

// —— Cache enumeration / read ——

const findAudioInData = (data: unknown, id: string): AudioItem | undefined => {
  if (!Predicate.isObjectKeyword(data)) return undefined;
  if ('pages' in data) {
    for (const page of (data as InfiniteData<PageResult>).pages) {
      const hit = page.items.find((i) => i.id === id);
      if (hit) return hit;
    }
    return undefined;
  }
  if ('items' in data && Array.isArray((data as PageResult).items)) {
    return (data as PageResult).items.find((i) => i.id === id);
  }
  if ('id' in data && (data as AudioRowLike).id === id) {
    return data as AudioItem;
  }
  return undefined;
};

// First occurrence of an audio row across any audio cache. Used by non-hook
// callsites and to build nested optimistic patches (e.g. `sharing`).
export const getAudioFromAnyCache = (
  queryClient: QueryClient,
  id: string,
): AudioItem | undefined => {
  for (const cache of queryClient
    .getQueryCache()
    .findAll({ queryKey: audioItemsBaseKey })) {
    const hit = findAudioInData(cache.state.data, id);
    if (hit) return hit;
  }
  return undefined;
};

// —— Apply / snapshot / restore across all audio caches ——

export type WalkSnapshot = Array<{
  queryKey: readonly unknown[];
  prevData: unknown;
}>;

export const applyAudioCacheOp = (
  queryClient: QueryClient,
  op: AudioCacheOp,
): WalkSnapshot => {
  const snapshot: WalkSnapshot = [];
  for (const cache of queryClient
    .getQueryCache()
    .findAll({ queryKey: audioItemsBaseKey })) {
    const prev = cache.state.data;
    if (prev === undefined) continue;
    const next = applyOpToData(prev, op);
    if (next === prev) continue;
    snapshot.push({ queryKey: cache.queryKey, prevData: prev });
    queryClient.setQueryData(cache.queryKey, next);
  }
  return snapshot;
};

export const restoreAudioCacheSnapshot = (
  queryClient: QueryClient,
  snapshot: WalkSnapshot,
): void => {
  for (const { queryKey, prevData } of snapshot) {
    queryClient.setQueryData(queryKey, prevData);
  }
};

export const cancelAudioCaches = (queryClient: QueryClient): Promise<void> =>
  queryClient.cancelQueries({ queryKey: audioItemsBaseKey });

export const invalidateAudioCaches = (
  queryClient: QueryClient,
): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: audioItemsBaseKey });

// —— Optimistic lifecycle (mirrors optimistic-list-entity-lifecycle's
//    two-flavor pattern: pure factory for tests + hook for production) ——

type WalkContext = { snapshot: WalkSnapshot };

type DeriveOp<V> = (
  variables: V,
  queryClient: QueryClient,
) => AudioCacheOp | null;

type WalkLifecycle<V> = {
  onMutate: (v: V, ctx: MutationFunctionContext) => Promise<WalkContext>;
  onError: (
    err: unknown,
    v: V,
    onMutateResult: WalkContext | undefined,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
  onSettled: (
    data: unknown,
    err: unknown,
    v: V,
    onMutateResult: unknown,
    ctx: MutationFunctionContext,
  ) => Promise<void>;
};

export function buildAudioWalkLifecycle<V>(opts: {
  runtime: AppRuntime;
  deriveOp: DeriveOp<V>;
}): WalkLifecycle<V> {
  const { runtime, deriveOp } = opts;
  return {
    onMutate: (v, ctx) =>
      runtime.runPromise(
        Effect.gen(function* () {
          yield* Effect.promise(() => cancelAudioCaches(ctx.client));
          const op = deriveOp(v, ctx.client);
          const snapshot = op ? applyAudioCacheOp(ctx.client, op) : [];
          return { snapshot };
        }),
      ),
    onError: (_err, _v, onMutateResult, ctx) =>
      runtime.runPromise(
        Effect.sync(() => {
          if (onMutateResult) {
            restoreAudioCacheSnapshot(ctx.client, onMutateResult.snapshot);
          }
        }),
      ),
    onSettled: (_d, _e, _v, _r, ctx) =>
      runtime.runPromise(
        Effect.promise(() => invalidateAudioCaches(ctx.client)),
      ),
  };
}

export function useAudioWalkLifecycle<V>(
  deriveOp: DeriveOp<V>,
): WalkLifecycle<V> {
  return buildAudioWalkLifecycle({ runtime: useAppRuntime(), deriveOp });
}

// —— Unarchive: a cross-list MEMBERSHIP move, not a field patch ——
//
// Unarchiving re-initializes an archived (otherwise-`complete`) audio: the row
// leaves every ready list (library / folders / continue / uploads) and re-enters
// the processing queue as `initializing`. `buildAudioWalkLifecycle`'s
// `patchFields` can only edit a row in place — it can't REMOVE it from the ready
// lists nor INSERT it into the processing queue — so unarchive gets its own
// lifecycle. This restores the pre-migration optimistic behavior (old
// `use-unarchive-audio.ts`: remove-from-library + insert-at-top-of-processing).
//
// The stamp comes from the runtime's Clock, so tests pin it through the Clock.
export function buildUnarchiveAudioLifecycle(opts: {
  runtime: AppRuntime;
}): WalkLifecycle<string> {
  const { runtime } = opts;
  return {
    onMutate: (audioId, ctx) =>
      runtime.runPromise(
        Effect.gen(function* () {
          const qc = ctx.client;
          yield* Effect.promise(() => cancelAudioCaches(qc));

          // The re-initialized row, built from wherever the archived row lives.
          const existing = getAudioFromAnyCache(qc, audioId);
          const stamp = yield* Clock.currentTimeMillis;
          const reinitPatch: Partial<AudioItem> = {
            conversion_status: 'initializing',
            created_at_ms: stamp,
            archived_at_ms: null,
          };

          // 1. Remove the row from every LIST cache (it's no longer ready);
          //    `removeById` is a no-op on the single detail/summary objects.
          const listSnapshot = applyAudioCacheOp(qc, {
            kind: 'removeById',
            id: audioId,
          });
          // 2. Patch the row in place wherever it survived as a single object
          //    (open detail/summary), so the player reflects the new status.
          const detailSnapshot = applyAudioCacheOp(qc, {
            kind: 'patchFields',
            id: audioId,
            partial: reinitPatch,
          });

          // 3. Insert the re-initialized row at the top of the processing queue
          //    — but only when that cache is already loaded; otherwise the
          //    onSettled invalidate reconciles it on next mount.
          const processing = qc.getQueryData<InfiniteData<PageResult>>(
            processingQueueQueryKey,
          );
          const firstPage = processing?.pages[0];
          const processingSnapshot: WalkSnapshot = [];
          if (existing && processing && firstPage) {
            processingSnapshot.push({
              queryKey: processingQueueQueryKey,
              prevData: processing,
            });
            const reinitRow: AudioItem = { ...existing, ...reinitPatch };
            qc.setQueryData<InfiniteData<PageResult>>(processingQueueQueryKey, {
              ...processing,
              pages: [
                { ...firstPage, items: [reinitRow, ...firstPage.items] },
                ...processing.pages.slice(1),
              ],
            });
          }

          return {
            snapshot: [
              ...listSnapshot,
              ...detailSnapshot,
              ...processingSnapshot,
            ],
          };
        }),
      ),
    onError: (_err, _v, onMutateResult, ctx) =>
      runtime.runPromise(
        Effect.sync(() => {
          if (onMutateResult) {
            restoreAudioCacheSnapshot(ctx.client, onMutateResult.snapshot);
          }
        }),
      ),
    onSettled: (_d, _e, _v, _r, ctx) =>
      runtime.runPromise(
        Effect.promise(() => invalidateAudioCaches(ctx.client)),
      ),
  };
}

export function useUnarchiveAudioLifecycle(): WalkLifecycle<string> {
  return buildUnarchiveAudioLifecycle({ runtime: useAppRuntime() });
}
